import { CircleIcon } from '@phosphor-icons/react';

import polygonToolGlyph from '@/assets/icons/polygon-tool.svg';

import type { FieldShapeType } from '../schemas';

import styles from './mapping-toolbar.module.scss';
import { UndoButton } from './undo-button';

export type MappingToolbarProps = {
  /** The active **Shape mode**. `polygon` on arrival (113600, Field Mapping Screen Elements). */
  mode: FieldShapeType;
  /** The Grower picked a tool. Ticket 11 raises the "Switch to Circle?" confirmation from here when
   *  a shape is already marked; this component never decides, so that lands with no reshaping. */
  onModeChange: (mode: FieldShapeType) => void;
  /** Whether the snapshot stack has anything to pop. Ticket 08 supplies the real value; until then
   *  nothing is markable, so it is always `false`. */
  canUndo: boolean;
  onUndo: () => void;
};

/**
 * Field Mapping mode's top-inline-end cluster — **Undo** and the two **Shape mode** tools (Figma
 * `587:524140` and `587:523586`).
 *
 * Presentational by construction: it holds no state, so the active tool and the undo history have
 * exactly one home (the page), and this component cannot disagree with the canvas about either.
 *
 * The tools are plain `<button aria-pressed>`s rather than a `<RadioGroup>` or `<Tabs>`: they are
 * not a form control and not navigation — they are two toggles over the same piece of state, and
 * `aria-pressed` says precisely that.
 *
 * The polygon glyph is a committed **export**, not a phosphor icon: Figma's `fi_12355988` is a
 * two-tone vector (white fill, black stroke) and every phosphor candidate is single-colour. The
 * circle glyph IS `CircleIcon` — an exact match, so no asset is committed for it.
 */
function toolClassName(active: boolean) {
  return [styles.tool, active ? styles.selected : ''].filter(Boolean).join(' ');
}

export function MappingToolbar({ mode, onModeChange, canUndo, onUndo }: MappingToolbarProps) {
  return (
    <div className={styles.toolbar}>
      <UndoButton canUndo={canUndo} onUndo={onUndo} />

      <div className={styles.tools}>
        <button
          type="button"
          className={toolClassName(mode === 'polygon')}
          aria-label="Polygon"
          aria-pressed={mode === 'polygon'}
          onClick={() => onModeChange('polygon')}
        >
          <img src={polygonToolGlyph} alt="" className={styles.glyph} width={20} height={20} />
        </button>
        <button
          type="button"
          className={toolClassName(mode === 'circle')}
          aria-label="Circle"
          aria-pressed={mode === 'circle'}
          onClick={() => onModeChange('circle')}
        >
          <CircleIcon size={20} aria-hidden />
        </button>
      </div>
    </div>
  );
}
